import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table, Button, message, Space, Tag } from "antd";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { MenuOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import ConfirmStatusModal from "../models/ConfirmStatusModal";
import { RiDeleteBin6Line } from "react-icons/ri";
import PrimaryButton from "../Forms/PrimaryButton";
import EmptySection from "../Layout/EmptySection";

const DND_TYPE = "DragableBodyRow";

// Drag handle icon component
const DragHandle = () => <MenuOutlined className="cursor-grab text-gray-400" />;

// Delete button component
const DeleteButton = ({ onClick, isMarkedForDeletion }) => (
  <Button
    type="text"
    onClick={onClick}
    size="small"
    className="flex items-center justify-center"
    icon={
      <div
        className={`p-2 rounded-full text-white ${
          isMarkedForDeletion ? "bg-gray-400" : "bg-[#A33434]"
        }`}
      >
        <RiDeleteBin6Line size={16} />
      </div>
    }
  />
);

// Draggable row component
const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: DND_TYPE,
    drop: (item) => {
      if (item.index !== index) moveRow(item.index, index);
    },
  });

  const [, drag] = useDrag({
    type: DND_TYPE,
    item: { index },
  });

  drag(drop(ref));
  return <tr ref={ref} className={className} style={style} {...restProps} />;
};

const DragDropTable = ({
  columns,
  data = [],
  handleChange,
  handleRow,
  rowSelection,
  className = "",
  scroll,
  onSave,
  hideActions = false,
  defaultSortKey = "sort_newly",
}) => {
  const [internalData, setInternalData] = useState(data);
  const [visibleData, setVisibleData] = useState(data);
  const [deletedItems, setDeletedItems] = useState([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const { showPopup } = useGlobalModal();

  // Update state when data prop changes
  useEffect(() => {
    setInternalData(data);
    setVisibleData(data);
    setDeletedItems([]);
  }, [data]);

  // Move row and update order
  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = visibleData[dragIndex];
      const newData = update(visibleData, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      });

      setVisibleData(newData);
      setInternalData(
        newData.map((item, index) => ({
          ...item,
          product: {
            ...item.product,
            [defaultSortKey]: index + 1,
          },
        }))
      );
    },
    [visibleData, defaultSortKey]
  );

  // Show confirmation modal for deletion
  const showDeleteConfirm = useCallback((record, e) => {
    e.stopPropagation();
    setItemToDelete(record);
    setIsDeleteModalVisible(true);
  }, []);

  // Handle confirmed deletion
  const handleConfirmDelete = useCallback(() => {
    if (itemToDelete) {
      setVisibleData((prev) => prev.filter((item) => item.key !== itemToDelete.key));
      setDeletedItems((prev) => [...prev, itemToDelete]);
    }
    setIsDeleteModalVisible(false);
    setItemToDelete(null);
  }, [itemToDelete]);

  // Handle cancellation of deletion
  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalVisible(false);
    setItemToDelete(null);
  }, []);

  // Restore a deleted item
  const restoreItem = useCallback((record, e) => {
    e.stopPropagation();

    setDeletedItems((prev) => prev.filter((item) => item.key !== record.key));
    setVisibleData((prev) => [...prev, record]);

    message.info("Item restored.");
  }, []);

  // Prepare table columns with drag and delete functionality in the last column
  const tableColumns = React.useMemo(() => {
    // Create a combined column for both drag and delete actions
    const actionsColumn = {
      title: "Actions",
      key: "__actions__",
      width: 120,
      render: (_, record) => {
        const isMarkedForDeletion = deletedItems.some((item) => item.key === record.key);

        return (
          <Space size={16}>
            {" "}
            <DeleteButton
              onClick={(e) =>
                isMarkedForDeletion ? restoreItem(record, e) : showDeleteConfirm(record, e)
              }
              isMarkedForDeletion={isMarkedForDeletion}
            />
            <DragHandle />
          </Space>
        );
      },
    };

    // Return all original columns plus the actions column at the end
    return [...columns, !hideActions ? actionsColumn : null].filter(Boolean);
  }, [columns, deletedItems, showDeleteConfirm, restoreItem]);

  // Row click handler - only for the row, not for buttons
  const onRowClick = useCallback(
    (record, rowIndex) => ({
      onClick: (e) => {
        // Check if the click was on a button or drag handle
        const isButtonClick =
          e.target.closest("button") || e.target.closest(".ant-dropdown-trigger");
        const isDragHandle = e.target.closest(".anticon-menu");

        if (!isButtonClick && !isDragHandle) {
          handleRow?.(record, rowIndex);
        }
      },
      index: rowIndex,
      moveRow,
    }),
    [handleRow, moveRow]
  );

  // Save order and process deletions
  const handleSave = useCallback(() => {
    if (!onSave) return;

    // Prepare the payload with current order (excluding deleted items)
    const payload = internalData
      .filter((item) => !deletedItems.some((deleted) => deleted.key === item.key))
      .map((item, index) => ({
        product_id: item.product.product_id,
        sort_order: index + 1,
      }));

    // Prepare delete payload
    const deletePayload = deletedItems.map((item) => ({
      product_id: item.product.product_id,
      sort_no: item.product.sort_no,
      quality_code: item.product.quality_code,
    }));

    // Call the onSave function with both order changes and deletions
    onSave({
      order: payload,
      deletions: deletePayload,
    });
  }, [internalData, deletedItems, onSave]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Table
        columns={tableColumns}
        dataSource={visibleData}
        components={{ body: { row: DraggableBodyRow } }}
        onRow={onRowClick}
        locale={{
          emptyText: <EmptySection />,
        }}
        className={`custom-hover-table px-4 cursor-pointer ${className}`}
        rowClassName={(record) =>
          rowSelection?.selectedRowKeys?.includes(record.key)
            ? "ant-table-row-selected"
            : "custom-row"
        }
        onChange={handleChange}
        rowSelection={rowSelection}
        bordered={false}
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      <ConfirmStatusModal
        open={isDeleteModalVisible}
        onClose={handleCancelDelete}
        handleSubmit={handleConfirmDelete}
        title="Do you want to delete this item?"
        buttonTitle="Delete"
        width={460}
      />

      {/* Save Button - Only disabled if there's no data at all */}

      <div className="mt-4 mr-4 !text-right mb-6">
        {!hideActions && (
          <PrimaryButton
            text="Save Changes"
            onClick={handleSave}
            noIcon
            disabled={internalData.length === 0}
            className="ms-auto"
          />
        )}
      </div>
    </DndProvider>
  );
};

export default DragDropTable;
